import type { Benchmark, BenchmarkCreateRequest, BenchmarkUpdateRequest } from '@amarantos/api-client'
import i18n from 'i18next'
import type { FieldPath } from 'react-hook-form'
import { z } from 'zod/v3'

/**
 * The elevation is entered as text (HTML number inputs yield strings) and converted by the payload
 * mappers. Validated as required + numeric here so an empty field surfaces a message instead of
 * silently becoming `0` — a benchmark at elevation zero would poison every baseline levelled
 * against it (ADR-0009).
 */
const elevationSchema = z
	.string()
	.trim()
	.min(1, i18n.t('validation.benchmark_elevation_required'))
	.refine((value) => Number.isFinite(Number(value)), i18n.t('validation.benchmark_elevation_invalid'))

export const benchmarkFormSchema = z.object({
	name: z.string().trim().min(1, i18n.t('validation.benchmark_name_required')),
	elevation: elevationSchema,
})

export type BenchmarkFormValues = z.infer<typeof benchmarkFormSchema>

export const emptyBenchmarkFormValues: BenchmarkFormValues = {
	name: '',
	elevation: '',
}

export function mapBenchmarkToFormValues(benchmark: Benchmark): BenchmarkFormValues {
	return {
		name: benchmark.name,
		elevation: String(benchmark.elevation),
	}
}

export function toCreateBenchmarkPayload(values: BenchmarkFormValues): BenchmarkCreateRequest {
	return {
		name: values.name.trim(),
		elevation: Number(values.elevation),
	}
}

export function toUpdateBenchmarkPayload(values: BenchmarkFormValues): BenchmarkUpdateRequest {
	return {
		name: values.name.trim(),
		elevation: Number(values.elevation),
	}
}

/** Maps API (422) field names to the form fields they correspond to. */
export const benchmarkValidationFieldMap = {
	name: 'name',
	elevation: 'elevation',
} as const satisfies Record<string, FieldPath<BenchmarkFormValues>>
